
import { isLoot } from "../client/loot";
import { isAttackable } from "../client/target";

const PLAYER_KIND = 0;
const NPC_KIND = 2;
const BUILDING_KIND = 5;

export const CursorShape = {
  NORMAL: 0,
  ATTACK: 1,
  TARGET: 2,
  TALK: 3,
  CANT_GO: 4,
  PICK: 5,
  DOOR: 6,
  CHAIR: 7,
  MAGIC: 8,
  BUY: 9,
  SELL: 10,
  CAMERA_ROTATE: 11,
  HSIZE: 12,
  VSIZE: 13,
  HVSIZE: 14,
} as const;

export type CursorShape = (typeof CursorShape)[keyof typeof CursorShape];

/** Where `tools/build_cursors.py` writes the sprites. */
export const CURSOR_DIR = "/cursors";

export interface CursorSprite {
  /** The `.sub`'s own stem, which is the PNG's name. */
  readonly file: string;
  /** CSS hotspot, in pixels from the sprite's top-left. */
  readonly hotspotX: number;
  readonly hotspotY: number;
  readonly fallback: string;
}

export const CURSOR_SPRITES: Readonly<Record<CursorShape, CursorSprite>> = {
  [CursorShape.NORMAL]: { file: "cursor", hotspotX: 0, hotspotY: 0, fallback: "default" },
  [CursorShape.ATTACK]: { file: "cursor_attack", hotspotX: 0, hotspotY: 0, fallback: "crosshair" },
  [CursorShape.TARGET]: { file: "cursor_attack", hotspotX: 0, hotspotY: 0, fallback: "crosshair" },
  [CursorShape.TALK]: { file: "cursor_talk", hotspotX: 0, hotspotY: 0, fallback: "help" },
  [CursorShape.CANT_GO]: { file: "cursor_no", hotspotX: 0, hotspotY: 0, fallback: "not-allowed" },
  [CursorShape.PICK]: { file: "cursor_pick", hotspotX: 0, hotspotY: 0, fallback: "grab" },
  [CursorShape.DOOR]: { file: "cursor_door", hotspotX: 0, hotspotY: 0, fallback: "pointer" },
  [CursorShape.CHAIR]: { file: "cursor_chair", hotspotX: 0, hotspotY: 0, fallback: "pointer" },
  [CursorShape.MAGIC]: { file: "cursor_chair", hotspotX: 0, hotspotY: 0, fallback: "pointer" },
  [CursorShape.BUY]: { file: "cursor_buy", hotspotX: 0, hotspotY: 0, fallback: "pointer" },
  [CursorShape.SELL]: { file: "cursor_sell", hotspotX: 0, hotspotY: 0, fallback: "pointer" },
  [CursorShape.CAMERA_ROTATE]: {
    file: "cursor_camera_rotate",
    hotspotX: 0,
    hotspotY: 0,
    fallback: "grabbing",
  },
  // `(-16, -16)` in `cursorPosDict` (`mousemodule.py:124-126`).
  [CursorShape.HSIZE]: { file: "cursor_hsize", hotspotX: 16, hotspotY: 16, fallback: "ew-resize" },
  [CursorShape.VSIZE]: { file: "cursor_vsize", hotspotX: 16, hotspotY: 16, fallback: "ns-resize" },
  [CursorShape.HVSIZE]: {
    file: "cursor_hvsize",
    hotspotX: 16,
    hotspotY: 16,
    fallback: "nwse-resize",
  },
};

/** The `cursor:` value for one shape, url first and keyword last. */
export function cursorCss(shape: CursorShape): string {
  const sprite = CURSOR_SPRITES[shape];
  return `url(${CURSOR_DIR}/${sprite.file}.png) ${sprite.hotspotX} ${sprite.hotspotY}, ${sprite.fallback}`;
}

export function isContinuousShape(shape: CursorShape): boolean {
  return (
    shape === CursorShape.NORMAL ||
    shape === CursorShape.ATTACK ||
    shape === CursorShape.TARGET ||
    shape === CursorShape.MAGIC ||
    shape === CursorShape.BUY ||
    shape === CursorShape.SELL
  );
}

export interface HoverSubject {
  readonly kind: number;
  readonly hp: number;
  readonly maxHp: number;
}

export interface HoverContext {
  /** Whether this entity is a player standing behind a stall sign. */
  readonly hasStall?: boolean;
}

export function hoverShape(
  subject: HoverSubject | null,
  context: HoverContext = {},
): CursorShape {
  if (subject === null) return CursorShape.NORMAL;
  if (isLoot(subject)) return CursorShape.PICK;
  if (subject.kind === PLAYER_KIND && context.hasStall === true) return CursorShape.BUY;
  if (subject.kind === NPC_KIND || subject.kind === BUILDING_KIND) return CursorShape.TALK;
  if (isAttackable(subject)) return CursorShape.ATTACK;
  return CursorShape.NORMAL;
}

/** The one thing `CursorController` writes to. `HTMLElement` satisfies it. */
export interface CursorTarget {
  readonly style: { cursor: string };
}

export class CursorController {
  /** `m_iCursorNum` (`PythonApplication.cpp:79`) -- the mode, not the paint. */
  private mode: CursorShape = CursorShape.NORMAL;
  /** `m_iContinuousCursorNum` (`PythonApplication.cpp:80`). */
  private continuous: CursorShape = CursorShape.NORMAL;
  /** What the pointer is over in the world, or `NORMAL` for nothing. */
  private hovered: CursorShape = CursorShape.NORMAL;
  /** What is actually on the element. */
  private shown: CursorShape = CursorShape.NORMAL;

  constructor(private readonly target: CursorTarget) {
    this.target.style.cursor = cursorCss(this.shown);
  }

  /** Which shape is on screen. */
  get current(): CursorShape {
    return this.shown;
  }

  /** The mode the pointer is working inside -- `m_iCursorNum`. */
  get modeShape(): CursorShape {
    return this.mode;
  }

  /** Which shape a `NORMAL` would restore. `m_iContinuousCursorNum`. */
  get restores(): CursorShape {
    return this.continuous;
  }

  /** `SetCursorNum`. Returns the mode actually set, which is not always the one
   *  asked for. */
  set(next: CursorShape): CursorShape {
    let want = next;
    if (next === CursorShape.NORMAL) {
      if (!isContinuousShape(this.mode)) want = this.continuous;
    } else if (isContinuousShape(this.mode)) {
      this.continuous = this.mode;
    }
    this.mode = want;
    this.paint();
    return want;
  }

  /** What the pointer is over, in one call -- `main.ts`'s pointer-move block.
   *  `null` for open ground, and for the pointer leaving the world entirely. */
  hover(subject: HoverSubject | null, context: HoverContext = {}): CursorShape {
    this.hovered = hoverShape(subject, context);
    this.paint();
    return this.shown;
  }

  private paint(): void {
    const want = this.hovered === CursorShape.NORMAL ? this.mode : this.hovered;
    if (want === this.shown) return;
    this.shown = want;
    this.target.style.cursor = cursorCss(want);
  }
}
